import { useState, useMemo } from "react";
import { useProfile } from "../../actprep-ui/context/ProfileContext";
import { useGet } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Chip,
  IconButton,
  Badge,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { DateRangePicker } from "@mui/x-date-pickers-pro";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";
import { capitalize, fullName } from "../../actprep-ui/utils/formatting";
import api from "../../actprep-ui/utils/api";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";
import { useConfirm } from "material-ui-confirm";
import dayjs from "dayjs";

export default function EmployeeCoverageManagement({ onUpdate }) {
  const { profile } = useProfile();
  const adminProfile = profile.admin;

  // Fetch all coverages (with related coverer/coveree pulled in)
  const { data: coverageData, refetch } = useGet(
    apiUrls.admin.employeeCoverage(),
    {
      params: { keep_related: true },
    }
  );
  const allCoverages = useMemo(() => coverageData?.data || [], [coverageData]);

  // Tab state
  const [tabIndex, setTabIndex] = useState(0);

  // Memoize filtered lists
  const pendingAsCoverer = useMemo(
    () =>
      allCoverages.filter(
        (c) => c.coverer.uuid === adminProfile.uuid && c.status === "pending"
      ),
    [allCoverages, adminProfile.uuid]
  );

  const acceptedCovering = useMemo(
    () =>
      allCoverages.filter(
        (c) => c.coverer.uuid === adminProfile.uuid && c.status === "accepted"
      ),
    [allCoverages, adminProfile.uuid]
  );

  const acceptedCoveredBy = useMemo(
    () =>
      allCoverages.filter(
        (c) => c.coveree.uuid === adminProfile.uuid && c.status === "accepted"
      ),
    [allCoverages, adminProfile.uuid]
  );

  const myRequests = useMemo(
    () => allCoverages.filter((c) => c.coveree.uuid === adminProfile.uuid),
    [allCoverages, adminProfile.uuid]
  );

  // New Request form state
  const [newCoverer, setNewCoverer] = useState(null);
  const [newDateRange, setNewDateRange] = useState([null, null]);
  const confirm = useConfirm();

  // Placeholder callbacks
  const handleAccept = async (coverage) => {
    const isConfirmed = await new Promise((resolve) =>
      confirm({
        title: "Are you sure?",
        description: `Are you sure you want to accept this delegation request? You will be responsible for ${fullName(
          coverage.coveree.user
        )}'s responsibilities from ${dayjs(coverage.start_date)
          .toDate()
          .toLocaleDateString()} to ${dayjs(coverage.end_date)
          .toDate()
          .toLocaleDateString()}.`,
      })
        .then(() => resolve(true))
        .catch(() => resolve(false))
    );
    if (!isConfirmed) {
      return;
    }
    const response = await api.put(
      apiUrls.admin.employeeCoverage(coverage.uuid),
      {
        status: "accepted",
      }
    );
    if (response.status == 200) {
      showSuccessSnackbar("Request accepted!");
      refetch();
      if (onUpdate) {
        onUpdate();
      }
    }
  };
  const handleReject = async (coverage) => {
    const isConfirmed = await new Promise((resolve) =>
      confirm({
        title: "Are you sure?",
        description:
          "Are you sure you want to reject this delegation request? This cannot be undone.",
      })
        .then(() => resolve(true))
        .catch(() => resolve(false))
    );
    if (!isConfirmed) {
      return;
    }
    const response = await api.put(
      apiUrls.admin.employeeCoverage(coverage.uuid),
      {
        status: "rejected",
      }
    );
    if (response.status == 200) {
      showSuccessSnackbar("Request rejected!");
      refetch();
      if (onUpdate) {
        onUpdate();
      }
    }
  };
  const handleDelete = async (coverage) => {
    const isConfirmed = await new Promise((resolve) =>
      confirm({
        title: "Are you sure?",
        description:
          "Are you sure you want to delete this delegation request? If it has already been accepted, you will have to request and wait on acceptance again if you change your mind.",
      })
        .then(() => resolve(true))
        .catch(() => resolve(false))
    );
    if (!isConfirmed) {
      return;
    }
    const response = await api.delete(
      apiUrls.admin.employeeCoverage(coverage.uuid)
    );
    if (response.status == 200) {
      showSuccessSnackbar("Request deleted!");
      refetch();
      if (onUpdate) {
        onUpdate();
      }
    }
  };
  const handleCreate = async () => {
    const payload = {
      coverer_id: newCoverer?.uuid,
      start_date: newDateRange[0].format("YYYY-MM-DD"),
      end_date: newDateRange[1].format("YYYY-MM-DD"),
    };
    const response = await api.post(apiUrls.admin.employeeCoverage(), payload);
    if (response.status == 200) {
      showSuccessSnackbar("Request created!");
      setTabIndex(2);
      refetch();
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Tabs
        value={tabIndex}
        onChange={(e, v) => setTabIndex(v)}
        aria-label="Employee Coverage Management Tabs"
        variant="scrollable"
      >
        <Tab
          label={
            <Badge
              badgeContent={pendingAsCoverer.length}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  left: -5,
                  top: -5,
                },
              }}
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              Pending to Approve
            </Badge>
          }
        />

        <Tab label="Upcoming Coverages" />
        <Tab label="My Requests" />
        <Tab label="New Request" />
      </Tabs>

      {/* 1. Pending as Coverer */}
      {tabIndex === 0 && (
        <Box sx={{ p: 2 }}>
          {pendingAsCoverer.length === 0 ? (
            <Typography>No pending requests.</Typography>
          ) : (
            <List>
              {pendingAsCoverer.map((c) => (
                <ListItem
                  key={c.uuid}
                  secondaryAction={
                    <Box>
                      <Button
                        onClick={() => handleAccept(c)}
                        variant="contained"
                        size="small"
                      >
                        Accept
                      </Button>
                      <Button
                        onClick={() => handleReject(c)}
                        variant="outlined"
                        size="small"
                        sx={{ ml: 1 }}
                      >
                        Reject
                      </Button>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={`${c.coveree.user.first_name} ${c.coveree.user.last_name}`}
                    secondary={`${c.start_date} → ${c.end_date}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}

      {/* 2. Upcoming Accepted Coverages */}
      {tabIndex === 1 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">I’m Covering</Typography>
          {acceptedCovering.length === 0 ? (
            <Typography sx={{ mb: 2 }}>None.</Typography>
          ) : (
            <List>
              {acceptedCovering.map((c) => (
                <ListItem key={c.uuid}>
                  <ListItemText
                    primary={`${c.coveree.user.first_name} ${c.coveree.user.last_name}`}
                    secondary={`${c.start_date} → ${c.end_date}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
          <Typography variant="h6" sx={{ mt: 3 }}>
            Covering Me
          </Typography>
          {acceptedCoveredBy.length === 0 ? (
            <Typography>None.</Typography>
          ) : (
            <List>
              {acceptedCoveredBy.map((c) => (
                <ListItem key={c.uuid}>
                  <ListItemText
                    primary={`${c.coverer.user.first_name} ${c.coverer.user.last_name}`}
                    secondary={`${c.start_date} → ${c.end_date}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}

      {/* 3. My Requests (as Coveree) */}
      {tabIndex === 2 && (
        <Box sx={{ p: 2 }}>
          {myRequests.length === 0 ? (
            <Typography>You have no requests.</Typography>
          ) : (
            <List>
              {myRequests.map((c) => (
                <ListItem
                  key={c.uuid}
                  secondaryAction={
                    <IconButton onClick={() => handleDelete(c)}>
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={`${c.coverer.user.first_name} ${c.coverer.user.last_name}`}
                    secondary={`${c.start_date} → ${c.end_date}`}
                  />
                  <Chip
                    label={capitalize(c.status)}
                    size="small"
                    sx={{ mx: 2 }}
                    color={
                      c.status === "accepted"
                        ? "success"
                        : c.status === "denied"
                        ? "error"
                        : "default"
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}

      {/* 4. New Request Form */}
      {tabIndex === 3 && (
        <Box sx={{ p: 2, maxWidth: 500 }}>
          <ModelAutoComplete
            modelName={"AdminProfile"}
            label={"Select Coverer"}
            onChange={(e, v) => setNewCoverer(v)}
            value={newCoverer}
            sx={{ mb: 3 }}
          />

          <DateRangePicker
            startText="Start Date"
            endText="End Date"
            value={newDateRange}
            onChange={(newVal) => setNewDateRange(newVal)}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "standard",
              },
            }}
          />

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              disabled={!newCoverer || !newDateRange[0] || !newDateRange[1]}
              onClick={handleCreate}
            >
              Submit Request
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
